<template>
  <div class="m-collapse-item" :class="{ 'is-disabled': disabled }">
    <div
      class="m-collapse-item__header"
      :class="{
        'is-active': isActive,
        'is-disabled': disabled,
      }"
      @click="handleHeaderClick"
    >
      <div class="m-collapse-item__title">
        <slot name="title">{{ title }}</slot>
        <i v-if="iconClass" class="collapse-item-icon" :class="iconClass" @click.stop="iconClick" />
      </div>
      <div class="m-collapse-item__icon">
        <slot name="icon">
          <i class="icon-outlined_pull-down" :class="{ 'is-active': isActive }" />
        </slot>
      </div>
    </div>

    <transition name="m-collapse-transition">
      <div v-show="isActive" ref="exposureTarget" class="m-collapse-item__wrapper">
        <div class="m-collapse-item__content">
          <slot></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MCollapseItem',

  props: {
    title: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      required: true,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
    iconClass: {
      type: String,
      default: '',
    },
    iconClick: {
      type: Function,
      default: () => {
        console.log('iconClick')
      },
    },
    exposureTime: {
      type: Number,
      default: 5000,
    },
    // 曝光事件
    exposureEvent: {
      type: Function,
      default: () => {
        console.log('exposureEvent')
      },
    },
    /** isActive 任意变化时调用（含展开/收起），参数为当前展开状态 */
    changeEvent: {
      type: Function,
      default: undefined,
    },
    /** 点击折叠头部切换展开态时调用（不含其它途径导致的 isActive 变化），参数为切换后的 isActive */
    tabClick: {
      type: Function,
      default: undefined,
    },
  },

  data() {
    return {
      isActive: this.defaultExpanded,
      pageType: this.$route.query.pageType || '',
      exposureTimerId: null,
      exposureObserver: null,
      /** 当前「进入视口」这一趟是否已触发过曝光（离开后重置） */
      exposureFiredForCurrentStreak: false,
      exposureInViewport: false,
    }
  },

  watch: {
    isActive(val) {
      const onChange = this.changeEvent
      if (typeof onChange === 'function') {
        onChange(val)
      }
      if (!val) {
        this.clearExposureTimer()
        this.exposureFiredForCurrentStreak = false
        this.exposureInViewport = false
        this.teardownExposureObserver()
      } else {
        this.$nextTick(() => this.setupExposureObserver())
      }
    },
  },

  mounted() {
    if (this.isActive) {
      this.$nextTick(() => this.setupExposureObserver())
    }
  },

  beforeDestroy() {
    this.teardownExposureObserver()
    this.clearExposureTimer()
  },

  methods: {
    // 点击切换展开/折叠
    handleHeaderClick() {
      if (this.disabled) return
      this.isActive = !this.isActive
      const onTabClick = this.tabClick
      if (typeof onTabClick === 'function') {
        onTabClick(this.isActive)
      }
      this.$emit('change', this.isActive)
    },

    clearExposureTimer() {
      if (this.exposureTimerId != null) {
        clearTimeout(this.exposureTimerId)
        this.exposureTimerId = null
      }
    },

    teardownExposureObserver() {
      if (this.exposureObserver) {
        this.exposureObserver.disconnect()
        this.exposureObserver = null
      }
    },

    setupExposureObserver() {
      this.teardownExposureObserver()
      if (!this.isActive) return

      const el = this.$refs.exposureTarget
      if (!el || typeof IntersectionObserver === 'undefined') {
        return
      }

      this.exposureObserver = new IntersectionObserver(
        entries => {
          const entry = entries[0]
          if (!entry || !this.isActive) return

          const visible = entry.isIntersecting && entry.intersectionRatio > 0
          this.exposureInViewport = visible

          if (!visible) {
            this.clearExposureTimer()
            this.exposureFiredForCurrentStreak = false
            return
          }

          if (this.exposureFiredForCurrentStreak) return

          this.clearExposureTimer()
          const ms = Math.max(0, Number(this.exposureTime) || 0)
          this.exposureTimerId = setTimeout(() => {
            this.exposureTimerId = null
            if (!this.isActive || !this.exposureInViewport) return
            this.exposureFiredForCurrentStreak = true
            const fn = this.exposureEvent
            if (typeof fn === 'function') {
              fn()
            }
          }, ms)
        },
        { root: null, threshold: 0 },
      )

      this.exposureObserver.observe(el)
    },
  },
}
</script>

<style scoped lang="scss">
/* 移动端优先 - 大字体、大点击区域 */
.m-collapse-item {
  /* width: 100%; */
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  border-radius: 24px;
  overflow: hidden;
  margin: 32px;
}

/* 头部区域 - 加大内边距，提高点击舒适度 */
.m-collapse-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 32px;
  min-height: 64px; /* 保证足够的点击区域 */
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: #fff;
  -webkit-tap-highlight-color: transparent;
  user-select: none; /* 避免移动端长按选中文字 */
}

/* 移动端点击反馈 */
.m-collapse-item__header:active {
  background-color: #f8f9fa;
}

.m-collapse-item__header.is-disabled {
  cursor: not-allowed;
  color: #adb5bd;
  background-color: #f8f9fa;
}

.m-collapse-item__header.is-disabled:active {
  background-color: #f8f9fa;
}

/* 标题文字 - 移动端大字号 */
.m-collapse-item__title {
  flex: 1;
  font-size: 30px;
  line-height: 1.4;
  color: #282b33;
  word-break: break-word;
  font-family: 'DiDi Sans Pro';
  font-weight: 700;
}

/* 图标区域 - 扩大点击区域 */
.m-collapse-item__icon {
  margin-left: 12px;
  padding: 8px 0 8px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 图标样式 - 旋转动画 */
.m-collapse-item__icon i {
  display: inline-block;
  font-size: 32px;
  color: #64748b;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(0deg);
}

.m-collapse-item__icon i.is-active {
  transform: rotate(180deg);
}

/* 内容区域 */
.m-collapse-item__wrapper {
  overflow: hidden;
}

.m-collapse-item__content {
  margin: 0 32px 32px 32px;
  font-size: 30px;
  line-height: 1.6;
  color: #475569;
  background-color: #fff;
  word-break: break-word;
  border-top: 1px solid #ebebeb;
}

/* 过渡动画 - 平滑展开收起 */
.m-collapse-transition-enter-active,
.m-collapse-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.m-collapse-transition-enter,
.m-collapse-transition-leave-to {
  opacity: 0;
  max-height: 0 !important;
}

.m-collapse-transition-enter-to,
.m-collapse-transition-leave {
  opacity: 1;
  max-height: 1000px !important;
}
.collapse-item-icon {
  position: relative;
  top: 7px;
  font-size: 32px;
  margin-left: 10px;
}
</style>
